import axios, { AxiosResponse } from "axios";
import { WebAPI, IFailCallbackParam } from "../constants";
import { getErrorStatus } from "./getErrorStatus";
import AuthService from "../../common/AuthService";
import { MacaddrMemo } from "./common";

interface ICallbackParams extends IFailCallbackParam {
  /**
   * WebAPIでMACアドレスのメモの更新が成功した場合に呼び出されます
   * @callback success
   * @param {number} status - httpステータス
   */
  success: (status: number) => void;
}

export interface IUpdateMacaddrRequestModel {
  /**
   * メモ
   */
  memo: string;
}

function AxiosPost(
  url: string,
  params: IUpdateMacaddrRequestModel,
  token?: string
) {
  if (token) {
    return axios.put(url, JSON.stringify(params), {
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
    });
  } else {
    return axios.put(url, JSON.stringify(params), {
      headers: { "Content-Type": "application/json" },
    });
  }
}

interface IAxiosInternalParams extends ICallbackParams {
  promise: Promise<AxiosResponse>;
}

function AxiosInternal(params: IAxiosInternalParams) {
  params.promise
    .then((res) => {
      params.success(res.status);
    })
    .catch((err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    });
}

export interface IUpdateMacaddrParams
  extends IUpdateMacaddrRequestModel,
    ICallbackParams {
  /**
   * 認証オブジェクト
   */
  authService?: AuthService;
  /**
   * MACアドレス
   */
  macaddr: string;
}

/**
 * MACアドレスのメモを更新します
 * @function updateMacaddr
 * @param {IUpdateMacaddrParams} params - MACアドレスメモ更新パラメータ
 */
export function updateMacaddr(params: IUpdateMacaddrParams): void {
  const { authService, macaddr, memo } = params;
  const req: IUpdateMacaddrRequestModel = {
    memo: MacaddrMemo(memo),
  };
  const url = WebAPI.UPDATE_MACADDR(macaddr);
  if (authService) {
    authService.getToken({
      success: (token) => {
        AxiosInternal({
          promise: AxiosPost(url, req, token),
          success: params.success,
          fail: (id, status) => {
            if (status === 401) {
              updateMacaddrRetry401(authService, url, req, params);
            } else {
              params.fail(id, status);
            }
          },
        });
      },
      fail: (err) => {
        const { id, status } = getErrorStatus(err);
        params.fail(id, status);
      },
    });
  } else {
    AxiosInternal({
      promise: AxiosPost(url, req),
      success: params.success,
      fail: params.fail,
    });
  }
}

/**
 * MACアドレスのメモを更新します
 * @function updateMacaddrRetry401
 * @param {AuthService} authService - 認証オブジェクト
 * @param {string} url - リクエストURL
 * @param {IUpdateMacaddrRequestModel} req - リクエストパラメータ
 * @param {ICallbackParams} params - コールバックパラメータ
 */
function updateMacaddrRetry401(
  authService: AuthService,
  url: string,
  req: IUpdateMacaddrRequestModel,
  params: ICallbackParams
): void {
  authService.updateToken({
    success: (token) => {
      AxiosInternal({
        promise: AxiosPost(url, req, token),
        success: params.success,
        fail: params.fail,
      });
    },
    fail: (err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    },
  });
}
